import React, { useEffect, useState } from 'react';
import InterruptorCatalogo from './InterruptorCatalogo';
import CampoTexto from './CampoTexto';
import CampoNumero from './CampoNumero';
import CampoDescripcionIA from './CampoDescripcionIA';
import CalculadoraPrecio from './CalculadoraPrecio';
import EtiquetaModal from './EtiquetaModal';
import MejorarConIAModal from './MejorarConIAModal';
import MejorarFotoNuevaModal from './MejorarFotoNuevaModal';
import { useCategorias } from '../context/CategoriasContext';
import { useProductos } from '../hooks/useProductos';
import { valoresDeDimension } from '../utils/agrupar';
import { validarProducto, normalizarProducto } from '../utils/validaciones';
import { subirFotos, subirFoto } from '../services/fotosService';
import { crearProducto, generarCodigoProducto } from '../services/productosService';
import { mensajeAmigable } from '../utils/errores';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { esDistribuidor } from '../utils/perfil';
import SelectorUnidad from './SelectorUnidad';
import SelectorImpuesto from './SelectorImpuesto';
import NuevaCategoriaProductoInline from './NuevaCategoriaProductoInline';
import { decimalesCantidad, textosCampoStock } from '../utils/unidades';
import { optimizarFoto } from '../utils/imagen';

// Los campos numéricos (stock y precios) usan CampoNumero: no dejan escribir
// letras ni símbolos, para que el inventario y los reportes cuadren.
const CAMPOS = [
  { label: 'Nombre del producto *', campo: 'nombre', tipo: 'text', max: 120 },
  { label: 'Marca', campo: 'marca', tipo: 'text', max: 60 },
  { label: 'Talla', campo: 'talla', tipo: 'text', max: 30 },
  { label: 'Color', campo: 'color', tipo: 'text', max: 40 },
  { label: 'Stock (cantidad)', campo: 'stock', tipo: 'numero', decimales: 0, min: 0, ayuda: 'Cuántas unidades tienes. Solo números enteros.' },
  { label: 'Precio de costo (L)', campo: 'precio_costo', tipo: 'numero', min: 0, prefijo: 'L' },
  { label: 'Precio de venta (L)', campo: 'precio_venta', tipo: 'numero', min: 0, prefijo: 'L' },
];

// Etiqueta, ayuda y decimales de un campo numérico: el stock y los precios
// cambian de texto si el producto se vende por libra o kilo.
function propsCampoNumero(c, producto) {
  const t = textosCampoStock(producto);
  if (c.campo === 'stock') {
    const dec = decimalesCantidad(producto);
    return { etiqueta: t.label, ayuda: t.ayuda, decimales: dec, placeholder: dec === 0 ? '0' : '0.00' };
  }
  const etiqueta = t.porUnidad ? c.label.replace(' (L)', `${t.porUnidad} (L)`) : c.label;
  return { etiqueta, ayuda: c.ayuda, decimales: c.decimales, placeholder: c.decimales === 0 ? '0' : '0.00' };
}

const PRODUCTO_VACIO = {
  nombre: '', categoria: '', subtipo: '', talla: '', color: '', descripcion: '',
  precio_costo: '', precio_venta: '', stock: '', marca: '', unidad: 'unidad', impuesto: 'g15', activo: true,
  foto_1: null, foto_2: null, foto_3: null, foto_4: null, foto_5: null,
};

export default function FormularioNuevoProducto({ onCerrar, onGuardado }) {
  const { categorias } = useCategorias();
  const { productos: todosProductos } = useProductos();
  const { negocio } = useAuth();
  // Perfil Distribuidor: los productos nuevos arrancan "Por libra".
  const productoInicial = esDistribuidor(negocio) ? { ...PRODUCTO_VACIO, unidad: 'lb' } : PRODUCTO_VACIO;
  const [producto, setProducto] = useState(productoInicial);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [productoGuardado, setProductoGuardado] = useState(null); // muestra la pantalla de éxito + etiqueta
  const [mostrarIA, setMostrarIA] = useState(false);
  // IA sobre la foto SUELTA (antes de guardar el producto). Se pueden generar
  // VARIAS imágenes del producto y todas se guardan en sus espacios de foto.
  const [fotosIA, setFotosIA] = useState([]);              // URLs ya generadas, listas para guardar
  const [imagenParaIA, setImagenParaIA] = useState(null);  // URL de la foto original subida (fuente para la IA)
  const [mostrarModalIA, setMostrarModalIA] = useState(false);
  const [subiendoIA, setSubiendoIA] = useState(false);
  const [mostrarEtiqueta, setMostrarEtiqueta] = useState(false);
  const [creandoCategoria, setCreandoCategoria] = useState(false);
  const [codigoPreview, setCodigoPreview] = useState(''); // código que se asignará al elegir categoría
  const { mostrarToast } = useToast();

  // Al elegir categoría, mostrar el código correlativo que recibirá el producto.
  useEffect(() => {
    let activo = true;
    if (!producto.categoria) { setCodigoPreview(''); return; }
    generarCodigoProducto(producto.categoria)
      .then((c) => { if (activo) setCodigoPreview(c); })
      .catch(() => { if (activo) setCodigoPreview(''); });
    return () => { activo = false; };
  }, [producto.categoria]);

  const actualizarCampo = (campo, valor) => {
    setProducto((p) => ({ ...p, [campo]: valor }));
    setErrores((e) => ({ ...e, [campo]: undefined }));
  };

  // Fotos elegidas actualmente, en orden (foto_1 = Principal).
  const fotosElegidas = [1, 2, 3, 4, 5].map((n) => producto[`foto_${n}`]).filter(Boolean);

  // Reparte una lista en los 5 espacios foto_1..foto_5 en orden.
  const guardarEnEspacios = (lista) => {
    const cambios = {};
    [1, 2, 3, 4, 5].forEach((n, i) => { cambios[`foto_${n}`] = lista[i] || null; });
    setProducto((p) => ({ ...p, ...cambios }));
    setErrores((e) => ({ ...e, foto_1: undefined }));
  };

  // Permite elegir VARIAS fotos de una sola vez (hasta completar 5), en el
  // orden en que se seleccionaron. Evita subirlas una por una.
  const agregarFotos = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    const espacio = 5 - fotosElegidas.length;
    if (espacio <= 0) { mostrarToast('Ya tienes 5 fotos, suficiente 😊', 'error'); return; }
    guardarEnEspacios([...fotosElegidas, ...files.slice(0, espacio)]);
    if (files.length > espacio) mostrarToast(`Se agregaron ${espacio}. El máximo es 5 fotos.`, 'error');
  };

  const quitarFoto = (idx) => guardarEnEspacios(fotosElegidas.filter((_, i) => i !== idx));

  // Desde la pantalla de éxito: limpiar el formulario para ingresar otro.
  const agregarOtro = () => {
    setProducto(productoInicial);
    setErrores({});
    setProductoGuardado(null);
    setFotosIA([]);
    setImagenParaIA(null);
    setMostrarModalIA(false);
    setMostrarIA(false);
  };

  const guardar = async () => {
    const nuevosErrores = validarProducto(producto, { esNuevo: true });
    if (Object.keys(nuevosErrores).length > 0) {
      setErrores(nuevosErrores);
      return;
    }
    setErrores({});
    setGuardando(true);
    try {
      // Si generamos fotos con IA, la Foto 1 original ya está subida (imagenParaIA):
      // no la re-subimos. Combinamos IA + originales (máx 5, sin repetir).
      const paraSubir = imagenParaIA ? { ...producto, foto_1: null } : producto;
      const fotosUrls = await subirFotos(paraSubir);
      const originales = [imagenParaIA, fotosUrls.foto_1_url, fotosUrls.foto_2_url, fotosUrls.foto_3_url, fotosUrls.foto_4_url, fotosUrls.foto_5_url];
      const todas = [...new Set([...fotosIA, ...originales].filter(Boolean))].slice(0, 5);
      const creado = await crearProducto({
        ...normalizarProducto(producto),
        foto_1_url: todas[0] || null,
        foto_2_url: todas[1] || null,
        foto_3_url: todas[2] || null,
        foto_4_url: todas[3] || null,
        foto_5_url: todas[4] || null,
      });
      const codigo = creado.codigo;
      mostrarToast(`¡Producto guardado! Código: ${codigo} 🎉`, 'exito');
      onGuardado(); // refresca el inventario detrás
      // Pantalla de éxito: usa el producto REAL creado (con id y URLs de fotos),
      // para poder imprimir etiqueta Y mejorar la foto con IA al instante.
      setProductoGuardado(creado);
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  // El campo "Tipo" está disponible para cualquier categoría (opcional): sirve
  // para sub-clasificar (ej. Ropa Interior: Brasieres, Panties; Calzado:
  // Tacones, Sandalias, Tenis). Sugerimos los tipos que ya existan en ella.
  // Sube la foto principal seleccionada y abre el mejorador de IA (foto suelta,
  // antes de guardar el producto).
  const abrirMejorarNueva = async () => {
    if (fotosIA.length >= 5) { mostrarToast('Ya tienes 5 fotos, suficiente 😊', 'error'); return; }
    if (imagenParaIA) { setMostrarModalIA(true); return; } // la foto ya se subió: reabrir el modal
    const archivo = producto.foto_1;
    if (!archivo) { mostrarToast('Primero sube la Foto 1 (Principal).', 'error'); return; }
    setSubiendoIA(true);
    try {
      const url = await subirFoto(archivo);
      setImagenParaIA(url);
      setMostrarModalIA(true);
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally { setSubiendoIA(false); }
  };
  // Cada foto elegida se suma; todas se guardan como fotos del producto al crear.
  const usarFotoIA = (url) => {
    setFotosIA((prev) => (prev.length >= 5 ? prev : [...prev, url]));
    mostrarToast('Foto agregada ✨ Puedes crear otra o cerrar.', 'exito');
  };

  const usaTipo = !!producto.categoria;
  const tiposSugeridos = usaTipo
    ? valoresDeDimension(todosProductos.filter((p) => p.categoria === producto.categoria), 'subtipo').valores
    : [];

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '32px', width: '500px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        {productoGuardado ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '46px', lineHeight: 1 }}>✅</div>
            <h2 style={{ margin: '10px 0 4px', color: 'var(--k-verde)' }}>¡Producto guardado!</h2>
            <p style={{ margin: '0 0 2px', fontSize: '15px', color: '#374151', fontWeight: 600 }}>{productoGuardado.nombre}</p>
            <p style={{ margin: '0 0 22px', fontSize: '13px', color: '#6B7280' }}>Código: <strong>{productoGuardado.codigo}</strong></p>
            <button onClick={() => setMostrarEtiqueta(true)}
              style={{ width: '100%', padding: '15px', backgroundColor: 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px' }}>
              🏷️ Imprimir etiqueta / código de barras
            </button>
            {[1, 2, 3, 4, 5].some((n) => productoGuardado[`foto_${n}_url`]) && (
              <button onClick={() => setMostrarIA(true)}
                style={{ width: '100%', padding: '13px', backgroundColor: 'color-mix(in srgb, var(--color-primario,#DB2777) 8%, white)', color: 'var(--color-primario,#DB2777)', border: '1px dashed var(--color-primario,#DB2777)', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px' }}>
                ✨ Mejorar la foto con IA
              </button>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <button onClick={agregarOtro}
                style={{ padding: '12px', backgroundColor: 'white', color: 'var(--color-primario,#DB2777)', border: '2px solid var(--color-primario,#DB2777)', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>
                ➕ Agregar otro
              </button>
              <button onClick={onCerrar}
                style={{ padding: '12px', backgroundColor: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>
                Listo
              </button>
            </div>
          </div>
        ) : (
        <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: 'var(--color-primario,#DB2777)' }}>Nuevo Producto</h2>
          <button onClick={onCerrar} disabled={guardando} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FFF5F7', borderRadius: '8px', border: '1px solid #FBCFE8' }}>
          <p style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: 'var(--color-primario,#DB2777)' }}>📸 Fotos del producto (opcionales, hasta 5 · elige varias a la vez · puedes agregarlas después)</p>
          {fotosElegidas.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {fotosElegidas.map((f, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={URL.createObjectURL(f)} alt="" style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                  <button type="button" onClick={() => quitarFoto(i)} disabled={guardando} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', border: 'none', background: '#B91C1C', color: '#fff', fontSize: '11px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                  {i === 0 && <span style={{ position: 'absolute', bottom: '2px', left: '2px', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '9px', padding: '1px 4px', borderRadius: '4px' }}>Principal</span>}
                </div>
              ))}
            </div>
          )}
          {fotosElegidas.length < 5 && (
            <input type="file" accept="image/*" multiple disabled={guardando}
              onChange={(e) => { agregarFotos(e.target.files); e.target.value = ''; }}
              style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }} />
          )}
        </div>

        {/* Mejorar/crear fotos con IA — apenas subes la Foto 1, antes de los datos.
            Puedes generar VARIAS y todas se guardan como fotos del producto. */}
        {(producto.foto_1 || fotosIA.length > 0) && (
          <div style={{ marginBottom: '16px' }}>
            {fotosIA.length > 0 && (
              <div style={{ padding: '10px 12px', borderRadius: '10px', background: 'color-mix(in srgb, var(--k-verde,#0D9488) 8%, white)', border: '1px solid color-mix(in srgb, var(--k-verde,#0D9488) 30%, white)', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>✨ {fotosIA.length} foto{fotosIA.length === 1 ? '' : 's'} con IA · se guardan en el producto</span>
                  <button type="button" onClick={() => setFotosIA([])} style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>quitar todas</button>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {fotosIA.map((u, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={optimizarFoto(u, 120)} alt={`IA ${i + 1}`} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                      <button type="button" onClick={() => setFotosIA(fotosIA.filter((_, j) => j !== i))} title="Quitar"
                        style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', border: 'none', background: '#B91C1C', color: '#fff', fontSize: '11px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {fotosIA.length < 5 && (
              <button type="button" onClick={abrirMejorarNueva} disabled={subiendoIA}
                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px dashed var(--color-primario,#DB2777)', background: 'color-mix(in srgb, var(--color-primario,#DB2777) 6%, white)', color: 'var(--color-primario,#DB2777)', fontSize: '14px', fontWeight: 700, cursor: subiendoIA ? 'wait' : 'pointer' }}>
                {subiendoIA ? '⏳ Preparando…' : (fotosIA.length > 0 ? '✨ Generar otra foto con IA' : '✨ Mejorar la foto con IA')}
              </button>
            )}
          </div>
        )}

        {CAMPOS.map((c) => (
          c.tipo === 'numero' ? (
            <div key={c.campo} style={{ marginBottom: '14px' }}>
              {c.campo === 'stock' && (
                <SelectorUnidad valor={producto.unidad} deshabilitado={guardando}
                  onChange={(v) => { actualizarCampo('unidad', v); setErrores((e) => ({ ...e, stock: undefined })); }} />
              )}
              <CampoNumero {...propsCampoNumero(c, producto)} valor={producto[c.campo]}
                min={c.min} prefijo={c.prefijo}
                error={errores[c.campo]} deshabilitado={guardando}
                onChange={(v) => actualizarCampo(c.campo, v)} />
            </div>
          ) : (
            <CampoTexto key={c.campo} label={c.label} tipo={c.tipo} valor={producto[c.campo]} maxLargo={c.max}
              error={errores[c.campo]} deshabilitado={guardando}
              onChange={(v) => actualizarCampo(c.campo, v)} />
          )
        ))}

        <SelectorImpuesto valor={producto.impuesto} deshabilitado={guardando}
          onChange={(v) => actualizarCampo('impuesto', v)} />

        <CalculadoraPrecio
          costo={producto.precio_costo}
          precioVenta={producto.precio_venta}
          onAplicarPrecio={(v) => actualizarCampo('precio_venta', v)}
          deshabilitado={guardando}
        />

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Categoría *</label>
          <select value={producto.categoria} disabled={guardando}
            onChange={(e) => (e.target.value === '__nueva__' ? setCreandoCategoria(true) : actualizarCampo('categoria', e.target.value))}
            style={{ width: '100%', padding: '10px', border: errores.categoria ? '1px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}>
            <option value="">Selecciona una categoría</option>
            {categorias.map((cat) => (
              <option key={cat.nombre} value={cat.nombre}>{cat.icono} {cat.nombre}</option>
            ))}
            <option value="__nueva__">➕ Crear categoría nueva…</option>
          </select>
          {creandoCategoria && (
            <NuevaCategoriaProductoInline onCerrar={() => setCreandoCategoria(false)}
              onCreada={(nombre) => { setCreandoCategoria(false); actualizarCampo('categoria', nombre); }} />
          )}
          {errores.categoria && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#EF4444' }}>⚠ {errores.categoria}</p>}
          {codigoPreview && (
            <div style={{ marginTop: '8px', padding: '10px 12px', backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>🏷️</span>
              <span style={{ fontSize: '13px', color: '#065F46' }}>Código que se le asignará: <strong style={{ fontSize: '15px', letterSpacing: '0.5px' }}>{codigoPreview}</strong></span>
            </div>
          )}
        </div>

        {usaTipo && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              Tipo <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(clasifica dentro de {producto.categoria})</span>
            </label>
            <input list="tipos-sugeridos" value={producto.subtipo} disabled={guardando}
              onChange={(e) => actualizarCampo('subtipo', e.target.value)}
              placeholder="Ej. Brasieres, Panties, Fajas talladoras, Accesorios…"
              style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
            <datalist id="tipos-sugeridos">
              {tiposSugeridos.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
        )}

        <CampoDescripcionIA
          valor={producto.descripcion}
          onChange={(v) => actualizarCampo('descripcion', v)}
          datosProducto={producto}
          deshabilitado={guardando}
        />

        <InterruptorCatalogo valor={producto.activo} onChange={(v) => actualizarCampo('activo', v)} deshabilitado={guardando} estilo={{ marginBottom: '16px' }} />

        {guardando && (
          <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#FEF3C7', borderRadius: '8px', border: '1px solid #FCD34D', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '13px', color: '#92400E', fontWeight: '600' }}>⏳ Guardando producto, por favor espera...</p>
          </div>
        )}

        <button onClick={guardar} disabled={guardando} style={{ width: '100%', padding: '14px', backgroundColor: guardando ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: guardando ? 'not-allowed' : 'pointer' }}>
          {guardando ? '⏳ Guardando...' : 'Guardar Producto'}
        </button>
        </>
        )}
      </div>

      {mostrarEtiqueta && productoGuardado && (
        <EtiquetaModal producto={productoGuardado} onCerrar={() => setMostrarEtiqueta(false)} />
      )}

      {mostrarIA && productoGuardado && (
        <MejorarConIAModal
          producto={productoGuardado}
          onCerrar={() => setMostrarIA(false)}
          onActualizado={(cambios) => { if (cambios) setProductoGuardado((p) => ({ ...p, ...cambios })); onGuardado && onGuardado(); }}
        />
      )}

      {mostrarModalIA && imagenParaIA && (
        <MejorarFotoNuevaModal
          imagenUrl={imagenParaIA}
          datosProducto={{ nombre: producto.nombre, categoria: producto.categoria, marca: producto.marca, talla: producto.talla, color: producto.color, subtipo: producto.subtipo }}
          agregadas={fotosIA.length}
          onUsar={usarFotoIA}
          onCerrar={() => setMostrarModalIA(false)}
        />
      )}
    </div>
  );
}
